import React, {PropsWithChildren, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useKeyPress} from '../../../hooks/useKeyPress';
import {useOnClickOutside} from '../../../incase-domain/utils/hooks/useOnClickOutside';

import {useLayout} from '../../../hooks/useLayout';
import Button from '../../../incase-domain/components/button/Button';

type ModalSize = 'lg' | 'md' | 'sm' | 'to-scale' | 'vh' | 'fit-content';

interface ModalProps {
    isOpen: boolean;
    header?: JSX.Element | string;
    showCancel?: boolean;
    onCancel?: () => void;
    cancelText?: string;
    onConfirm?: () => void;
    confirmText?: string;
    confirmDisabled?: boolean;
    size?: ModalSize;
    blockOverlayClose?: boolean;
    contentOnly?: boolean;
    delay?: number;
    footerAlignment?: 'center' | 'right';
    dataCy?: string;
}

function Modal(props: PropsWithChildren<ModalProps>): JSX.Element {
    const {
        isOpen,
        header,
        showCancel = false,
        onCancel,
        cancelText,
        onConfirm,
        confirmText,
        confirmDisabled,
        size,
        blockOverlayClose = false,
        contentOnly = false,
        delay = 0,
        footerAlignment = 'right',
        children,
        dataCy,
    } = props;

    const {t} = useTranslation();
    const {mobileView} = useLayout();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isOpen) {
            timer = setTimeout(() => setIsVisible(true), delay);
        } else {
            setIsVisible(false);
        }
        return () => clearTimeout(timer);
    }, [isOpen, delay]);

    const isEscPressed = useKeyPress('Escape');
    useEffect(() => {
        if (isEscPressed && isVisible) onCancel && onCancel();
    }, [isEscPressed, onCancel, isVisible]);

    const dropdownRef = useRef(null);
    useOnClickOutside(dropdownRef, () => isVisible && !blockOverlayClose && onCancel && onCancel());

    return (
        <div className={`c-modal c-modal--${size} ${isVisible ? 'is-open' : ''}`} aria-hidden={!isVisible} data-testid="modal">
            <div className="c-modal__overlay" tabIndex={-1} data-testid="modal-overlay">
                <div className="c-modal__body" role="dialog" aria-modal="true" ref={dropdownRef} data-testid="modal-body" data-cy={dataCy}>
                    <div className="c-modal__header">
                        <h2 className="c-modal__header_title">{header}</h2>
                        {!mobileView && onCancel && (
                            <Button
                                variant="ghost"
                                size="sm"
                                square
                                className="c-modal__close e-close is-lg u-flex u-v-center"
                                aria-label="Close"
                                onClick={onCancel}
                                data-testid="modal-close"
                            />
                        )}
                    </div>

                    <div className="c-modal__content" data-testid="modal-content">
                        {children}
                    </div>

                    {(showCancel || onConfirm || mobileView) && !contentOnly && (
                        <div className="c-modal__footer">
                            <div
                                className={`c-modal__footer_buttons ${
                                    footerAlignment === 'center' ? 'c-modal__footer_buttons--centerAlign' : ''
                                }`}
                            >
                                {(showCancel || mobileView) && (
                                    <Button variant="soft" color="secondary" size="md" onClick={onCancel}>
                                        {cancelText || t('common.cancel')}
                                    </Button>
                                )}

                                {onConfirm && (
                                    <Button
                                        variant="solid"
                                        color="secondary"
                                        size="md"
                                        onClick={onConfirm}
                                        disabled={confirmDisabled}
                                        dataCy="modal-confirm"
                                    >
                                        {confirmText || t('common.confirm')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Modal;
